import moment from 'moment';
import { G2S } from '../g2s';
import * as api from '../api';
import * as flights from '../flights';
import * as utils from '../utils';

// Global var for skyscanner tag
declare let sat: any;
// Global var for VK retargeting
declare let VK: any;

/**
 * Generates pixels for partners.
 */
export class PixelsService {
    static vkPriceListIdFlights: number | null = null;//1791;

    static setPixelForHomepage(target: HTMLElement) {
        const markerValue = utils.Cookies.getMarkerValue();

        // VK
        if (typeof VK !== 'undefined' && VK) {
            VK.Retargeting.Event('view_home');

            if (PixelsService.vkPriceListIdFlights) {
                VK.Retargeting.ProductEvent(PixelsService.vkPriceListIdFlights, 'view_home', markerValue ? {'products':[{'id':markerValue}]} : {});
            }
        }

        // Criteo
        this.setCriteoPixel(
            target,
            {
                viewHome: true
            }
        );
    }

    static setPixelForFlightBooking(target: HTMLElement, searchParams: flights.FlightSearchParams, billingPrice: number) {
        const productId = searchParams.from + '_' + searchParams.to;
        let productDates = 'checkin_date: "' + moment(searchParams.departure!).format('YYYY-MM-DD') + '"';
        if (searchParams.roundtrip) {
            productDates += ',checkout_date: "' + moment(searchParams.return!).format('YYYY-MM-DD') + '"';
        }
        const markerValue = utils.Cookies.getMarkerValue();

        // Criteo
        this.setCriteoPixel(
            target,
            {
                viewSearch: productDates,
                viewItem: '"' + productId + '"',
                viewBasket: '[ { id: "' + productId + '", price:' + Math.round(billingPrice) + ', quantity:' + (searchParams.passengers.adults + searchParams.passengers.children + searchParams.passengers.infants) + '} ]'
            }
        );

        // Skyscanner
        const skyScannerRedirectId = utils.Cookies.getSkyScannerRedirectId();
        if (skyScannerRedirectId) {
            const w: any = window;

            w['SkyscannerAnalyticsTag'] = 'sat';
            // eslint-disable-next-line prefer-rest-params
            w['sat'] = w['sat'] || function () { (w['sat'].buffer = w['sat'].buffer || []).push(arguments); };
            w['sat'].u = 'https://analytics.skyscanner.net';
            const l = document.createElement('script');
            l.src = 'https://analytics.skyscanner.net/tag.js';
            l.async = true;
            const h = document.getElementsByTagName('head')[0];
            h.appendChild(l);

            // Call new skyscanner tag
            sat('init', 'SAT-578300-1');
            sat('send', 'landing', {
                priceSeen: `${Math.round(billingPrice)}`,
                currency: G2S.config.currencySymbol,
                paymentOption: 'Card'
            });

            utils.Cookies.removeSkyScannerRedirectId();
        }

        // VK
        if (typeof VK !== 'undefined' && VK) {
            VK.Retargeting.Event('init_checkout');

            if (PixelsService.vkPriceListIdFlights) {
                VK.Retargeting.ProductEvent(PixelsService.vkPriceListIdFlights, 'init_checkout', markerValue ? {'products':[{'id':markerValue}]} : {});
            }
        }

        // Zendesk: display widget only on booking form
        if (G2S.config.environment === 'Production') {
            const el = document.createElement('script');
            el.type = 'text/javascript';
            el.async = true;
            el.id = 'ze-snippet';
            el.src = 'https://static.zdassets.com/ekr/snippet.js?key=881a11b2-3b26-4e80-905c-4807c5966f22';
            target.appendChild(el);
        }
    }

    static setPixelForFlightSearch(target: HTMLElement, searchParams: flights.FlightSearchParams) {
        const productId = searchParams.from + '_' + searchParams.to;
        let productDates = 'checkin_date: "' + moment(searchParams.departure!).format('YYYY-MM-DD') + '"';
        if (searchParams.roundtrip) {
            productDates += ',checkout_date: "' + moment(searchParams.return!).format('YYYY-MM-DD') + '"';
        }
        const markerValue = utils.Cookies.getMarkerValue();

        // Criteo
        this.setCriteoPixel(
            target,
            {
                viewSearch: productDates,
                viewItem: '"' + productId + '"'
            }
        );

        // VK
        if (typeof VK !== 'undefined' && VK) {
            VK.Retargeting.Event('view_search');

            if (PixelsService.vkPriceListIdFlights) {
                VK.Retargeting.ProductEvent(PixelsService.vkPriceListIdFlights, 'view_search', markerValue ? {'products':[{'id':markerValue}]} : {});
            }
        }
    }

    static setPixelForOrderConfirmation(target: HTMLElement, reservationInfo: api.ReservationInfo, flight: flights.FlightModel | null) {
        const reservationAmount = reservationInfo.amount.toFixed(2);

        // One-time pixles
        if (moment().diff(moment(reservationInfo.createdDate), 'hours') <= 24) {
            const orderOneTimeCookie = utils.Cookies.getOrderOneTimeCookie();
            const orderId = '[' + reservationInfo.reservationNumber + ']';
            let execPixel = true;

            if (orderOneTimeCookie
                && orderOneTimeCookie.indexOf(orderId) >= 0) {
                execPixel = false;
            }

            if (execPixel) {
                utils.Cookies.setOrderOneTimeCookie((orderOneTimeCookie ? orderOneTimeCookie : '') + orderId);

                const markerValue = utils.Cookies.getMarkerValue();

                if (flight) {
                    const productId = flight.legs[0].departureDestination.iataCode + '_' + flight.legs[0].arrivalDestination.iataCode;
                    let productDates = 'checkin_date: "' + moment(flight.legs[0].departureDate).format('YYYY-MM-DD') + '"';
                    if (flight.legs.length > 1) {
                        productDates += ',checkout_date: "' + moment(flight.legs[1].departureDate).format('YYYY-MM-DD') + '"';
                    }

                    // Criteo
                    this.setCriteoPixel(
                        target,
                        {
                            viewSearch: productDates,
                            trackTransaction: 'id: "' + reservationInfo.reservationNumber + '", item: [ { id: "' + productId + '", price: ' + reservationInfo.amount + ', quantity: ' + reservationInfo.passengers.length + '} ]'
                        }
                    );
                }

                // VK
                if (typeof VK !== 'undefined' && VK) {
                    VK.Retargeting.Event('purchase');

                    if (PixelsService.vkPriceListIdFlights) {
                        VK.Retargeting.ProductEvent(PixelsService.vkPriceListIdFlights, 'purchase',
                            {
                                'products': markerValue ? [{'id':markerValue}] : [],
                                'total_price' : reservationInfo.amount
                            });
                    }
                }
            }
        }

        // Skyscanner
        const skyScannerRedirectId = utils.Cookies.getSkyScannerRedirectId();
        if (skyScannerRedirectId) {
            const w: any = window;

            w['SkyscannerAnalyticsTag'] = 'sat';
            // eslint-disable-next-line prefer-rest-params
            w['sat'] = w['sat'] || function () { (w['sat'].buffer = w['sat'].buffer || []).push(arguments); };
            w['sat'].u = 'https://analytics.skyscanner.net';
            const l = document.createElement('script');
            l.src = 'https://analytics.skyscanner.net/tag.js';
            l.async = true;
            const h = document.getElementsByTagName('head')[0];
            h.appendChild(l);

            // Call new skyscanner tag
            sat('init', 'SAT-578300-1');
            sat('send', 'conversion', {
                bookingReference: reservationInfo.reservationNumber
            });

            utils.Cookies.removeSkyScannerRedirectId();
        }

        // Kayak
        if (reservationInfo.markerOwner
            && reservationInfo.markerOwner.id === 2207
            && reservationInfo.markerOwner.value) {
            const el = document.createElement('img');
            el.src = 'https://www.kayak.com/s/kayakpixel/confirm/go2see?'
                + 'kayakclickid=' + reservationInfo.markerOwner.value
                + '&price=' + reservationAmount
                + '&currency=' + G2S.config.currencySymbol
                + '&confirmation=' + reservationInfo.reservationNumber
                + '&rand=' + moment().unix();
            target.appendChild(el);
        }
    }

    static setPixelForPromotionRegistration(promotionId: string) {
        // VK
        if (typeof VK !== 'undefined' && VK) {
            VK.Retargeting.Event('promotion.' + promotionId + '.registration');
        }
    }

    private static setCriteoPixel(target: HTMLElement, pixelEvents: any) {
        const el = document.createElement('script');
        el.type = 'text/javascript';
        el.async = true;
        el.src = '//static.criteo.net/js/ld/ld.js';
        target.appendChild(el);

        let script = 'window.criteo_q = window.criteo_q || [];'
            + 'window.criteo_q.push('
            + '{ event: "setAccount", account: 56402 }'
            + ',{ event: "setSiteType", type: "' + (G2S.config.clientDevice.type === 'mobile' ? 'm' : 'd') + '" }'
            + ',{ event: "viewSearch", ' + pixelEvents.viewSearch + ' }';

        if (pixelEvents.viewItem) {
            script += ',{ event: "viewItem", item: ' + pixelEvents.viewItem + ' }';
        }

        if (pixelEvents.viewBasket) {
            script += ',{ event: "viewBasket", item: ' + pixelEvents.viewBasket + ' }';
        }

        if (pixelEvents.trackTransaction) {
            script += ',{ event: "trackTransaction", ' + pixelEvents.trackTransaction + ' }';
        }

        if (pixelEvents.viewHome) {
            script += ',{ event: "viewHome" }';
        }

        script += ');';

        try {
            eval(script);
        }
        catch {
            console.error('setCriteoPixel script error:', script);
        }
    }
}
